<script setup>
import { computed, ref, watch, nextTick, getCurrentInstance } from 'vue';
import {
  applyDigit,
  backspaceCents,
  centsToDisplay,
  centsToNumber,
  digitsFromPaste,
  digitsToCents,
  numberToCents,
} from 'kanban/helper/moneyInput';

const props = defineProps({
  modelValue: { type: [String, Number], default: null },
  customInputClass: { type: [String, Object, Array], default: '' },
  placeholder: { type: String, default: '0,00' },
  label: { type: String, default: '' },
  id: { type: String, default: '' },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md'].includes(value),
  },
  message: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  min: { type: [String, Number], default: 0 },
});

const emit = defineEmits([
  'update:modelValue',
  'blur',
  'input',
  'focus',
  'enter',
  'change',
]);

const { uid } = getCurrentInstance();
const uniqueId = computed(() => props.id || `input-${uid}`);

const isFocused = ref(false);
const inputRef = ref(null);
const cents = ref(numberToCents(props.modelValue));

const displayValue = computed(() => centsToDisplay(cents.value));

const minCents = computed(() => {
  if (props.min === '' || props.min === null || props.min === undefined) {
    return 0;
  }
  return numberToCents(props.min);
});

const clampCents = value => Math.max(minCents.value, value);

const messageClass = computed(() => {
  switch (props.messageType) {
    case 'error':
      return 'text-n-ruby-9 dark:text-n-ruby-9';
    case 'success':
      return 'text-n-teal-10 dark:text-n-teal-10';
    default:
      return 'text-n-slate-11 dark:text-n-slate-11';
  }
});

const inputOutlineClass = computed(() => {
  switch (props.messageType) {
    case 'error':
      return 'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 disabled:outline-n-ruby-8 dark:disabled:outline-n-ruby-8';
    default:
      return 'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 disabled:outline-n-weak dark:disabled:outline-n-weak focus:outline-n-brand dark:focus:outline-n-brand';
  }
});

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'h-8 !px-3 !py-2';
    case 'md':
      return 'h-10 !px-3 !py-2.5';
    default:
      return 'h-10 !px-3 !py-2.5';
  }
});

const placeCaretAtEnd = async () => {
  await nextTick();
  const el = inputRef.value;
  if (!el) return;
  const len = el.value.length;
  el.setSelectionRange(len, len);
};

const emitValue = () => {
  const numeric = centsToNumber(cents.value);
  emit('update:modelValue', numeric);
  emit('input', numeric);
};

watch(
  () => props.modelValue,
  value => {
    const next = clampCents(numberToCents(value));
    if (next !== cents.value) {
      cents.value = next;
    }
  }
);

const handleKeydown = event => {
  if (props.disabled) return;

  const { key } = event;

  if (key === 'Tab' || key === 'Enter' || key === 'Escape') return;

  if (
    event.metaKey ||
    event.ctrlKey ||
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    key === 'ArrowUp' ||
    key === 'ArrowDown' ||
    key === 'Home' ||
    key === 'End'
  ) {
    return;
  }

  if (key === 'Backspace' || key === 'Delete') {
    event.preventDefault();
    cents.value = clampCents(backspaceCents(cents.value));
    emitValue();
    placeCaretAtEnd();
    return;
  }

  if (/^\d$/.test(key)) {
    event.preventDefault();
    cents.value = clampCents(applyDigit(cents.value, key));
    emitValue();
    placeCaretAtEnd();
    return;
  }

  event.preventDefault();
};

const handlePaste = event => {
  if (props.disabled) return;
  event.preventDefault();
  const pasted = digitsFromPaste(event.clipboardData?.getData('text'));
  if (!pasted) return;
  cents.value = clampCents(digitsToCents(pasted));
  emitValue();
  placeCaretAtEnd();
};

const handleFocus = event => {
  emit('focus', event);
  isFocused.value = true;
  placeCaretAtEnd();
};

const handleBlur = event => {
  const numeric = centsToNumber(cents.value);
  emit('change', { target: { value: String(numeric) } });
  emit('blur', event);
  isFocused.value = false;
};

const handleEnter = event => {
  emit('enter', event);
};
</script>

<template>
  <div class="relative flex flex-col min-w-0 gap-1">
    <label
      v-if="label"
      :for="uniqueId"
      class="mb-0.5 text-heading-3 text-n-slate-12"
    >
      {{ label }}
    </label>
    <slot name="prefix" />
    <input
      :id="uniqueId"
      v-bind="$attrs"
      ref="inputRef"
      :value="displayValue"
      :class="[
        customInputClass,
        inputOutlineClass,
        sizeClass,
        {
          error: messageType === 'error',
          focus: isFocused,
        },
      ]"
      type="text"
      inputmode="numeric"
      autocomplete="off"
      :placeholder="placeholder"
      :disabled="disabled"
      class="block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 text-right transition-all duration-500 ease-in-out"
      @keydown="handleKeydown"
      @paste="handlePaste"
      @focus="handleFocus"
      @blur="handleBlur"
      @keyup.enter="handleEnter"
    />
    <p
      v-if="message"
      class="min-w-0 mt-1 mb-0 text-label-small truncate transition-all duration-500 ease-in-out"
      :class="messageClass"
    >
      {{ message }}
    </p>
  </div>
</template>
